import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useAuthStore from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Shield, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0a775788-3b72-4cac-99b0-2ae8be67d728/artifacts/5ne6bwob_Alhaj_Halfan_Swai_Logo.png';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      if (response.status === 200) {
        if (response.data.user.role !== 'admin') {
          toast.error('Access denied. Admin credentials required.');
          return;
        }
        
        setUser(response.data.user, response.data.token);
        toast.success('Admin login successful!');
        navigate('/admin');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-slate-800/90 backdrop-blur-md shadow-2xl border border-slate-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="AHSGA Logo" className="w-24 h-24 object-contain" />
            </div>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Shield className="w-6 h-6 text-orange-500" />
              <CardTitle className="text-3xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Admin Portal
              </CardTitle>
            </div>
            <h1 className="text-4xl font-extrabold text-white tracking-wide mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>AHSGA</h1>
            <p className="text-slate-400 mt-2 text-sm">Administrator Access Only</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="admin@ahsga.me"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-700 border-slate-600 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                {loading ? 'Signing in...' : 'Admin Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-400 hover:text-slate-200 block"
              >
                Forgot your password?
              </Link>
              <Link
                to="/member/login"
                className="text-sm text-slate-400 hover:text-slate-200 block"
              >
                Member? Login here
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          © 2026 AHSGA. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;